import { ContentType, GeneralDetailError } from '@kcftech/prometheus-client';
import {
    createContentPath,
    createScopedId,
    FileId,
    PrometheusFilesClientResponseError,
} from '@kcftech/prometheus-files-sdk';
import {
    mutationOptions,
    UseMutationOptions,
    UseMutationResult,
    useMutation,
    useMutationState,
} from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdFilesClient } from 'contexts/SDKClientContext';
import { FileUpload, PrintableError } from 'pages/Settings/FileLibrary/shared';
import { FileType, FileTypePattern, validateFileForUpload } from 'utils/file';
import { sentryCaptureException } from 'utils/sentry';
import { toRequiredScope } from '../path';

const BASE_MUTATION_KEY = ['file-upload'];

type FileUploadMutationConfig = {
    groupIds?: string[];
    locationId?: string;
    corporationId?: string;
    parentFolderId?: string;
    allowedFileTypes?: (FileType | FileTypePattern)[];
};

export const fileUploadMutations = {
    upload: ({
        groupIds,
        locationId,
        corporationId,
        parentFolderId,
        allowedFileTypes,
    }: FileUploadMutationConfig) =>
        mutationOptions({
            mutationKey: [
                ...BASE_MUTATION_KEY,
                { groupIds, locationId, corporationId },
            ] as const,
            mutationFn: async (file: FileUpload): Promise<FileId> => {
                const errorReason = validateFileForUpload(file, allowedFileTypes);
                if (errorReason) {
                    throw new PrintableError(`File upload failed: ${errorReason}`);
                }

                const scope = toRequiredScope(corporationId, locationId);
                const identifier = parentFolderId
                    ? {
                          folderId: createScopedId({
                              ...scope,
                              fileId: parentFolderId,
                          }),
                          fileName: file.name,
                      }
                    : createContentPath({
                          ...scope,
                          filePath: '/' + file.name,
                      });

                return await sdFilesClient.writeFileBody(
                    identifier,
                    {
                        GroupIds: groupIds ?? [],
                        MakeDirectories: true,
                        MediaType: file.type,
                        // todo @jp: consider not accepting FileType, just
                        //   have the backend process MediaType
                        FileType: file.type.startsWith('image/')
                            ? ContentType.Picture
                            : file.type.startsWith('video/')
                              ? ContentType.Video
                              : ContentType.GenericFile,
                        AutoRename: true,
                    },
                    file,
                );
            },
        }),
};

export type UseFileUpload = {
    /**
     * The id of the corporation to upload the file to.
     */
    corporationId?: string;
    /**
     * The id of the location to upload the file to.
     */
    locationId?: string;
    /**
     * The group Ids to associate with the file
     */
    groupIds?: string[];
    /**
     * Parent Folder's Id. If unsupplied, will use the Root folder's id
     */
    parentFolderId?: string;

    noToasts?: boolean;
    allowedFileTypes?: (FileType | FileTypePattern)[];
};

type UseFileUploadOptions = UseMutationOptions<
    FileId,
    GeneralDetailError | Error,
    FileUpload
>;

type UseFileUploadMutationResult = UseMutationResult<
    FileId,
    GeneralDetailError | Error,
    FileUpload,
    unknown
>;

export function useIsAnyFileUploading(): boolean {
    const mutationStates = useMutationState({
        filters: {
            mutationKey: BASE_MUTATION_KEY,
        },
    });

    return mutationStates.reduce(
        (acc, state) => acc || state.status === 'pending',
        false,
    );
}

export type UseFileUploadResult = {
    mutation: UseFileUploadMutationResult;
    errorMessage: string | undefined;
};

export function useFileUpload(
    {
        locationId,
        corporationId,
        groupIds,
        parentFolderId,
        noToasts,
        allowedFileTypes,
    }: UseFileUpload,
    options?: UseFileUploadOptions,
): UseFileUploadResult {
    const { showToast } = useNotificationContext();

    const mutation = useMutation({
        ...fileUploadMutations.upload({
            groupIds,
            locationId,
            corporationId,
            parentFolderId: parentFolderId,
            allowedFileTypes,
        }),
        ...options,
        onSuccess: (...args) => {
            const [_id, vars] = args;
            if (!noToasts) {
                showToast({
                    type: 'success',
                    content: `${vars.name} uploaded successfully.`,
                });
            }
            options?.onSuccess?.(...args);
        },
        onError: (...args) => {
            const [err, data] = args;
            options?.onError?.(...args);
            // eslint-disable-next-line no-console
            console.error(args[0]);

            if (
                err instanceof PrometheusFilesClientResponseError &&
                err.parsedBody?.status === 409
            ) {
                showToast({
                    type: 'error',
                    content: `Conflict while uploading '${data.name}' (does this file already exist?)`,
                });
                return;
            }

            if (!(err instanceof PrintableError)) {
                sentryCaptureException({
                    error: err,
                    tags: { hook: 'useFileUpload' },
                    context: { upload: { fileName: data.name, groupIds } },
                });
            }

            const message =
                err instanceof PrintableError ? err.message : 'An unknown error occurred';

            showToast({ type: 'error', content: message });
        },
    });

    return {
        mutation,
        errorMessage: mutation.isError
            ? mutation.error instanceof PrintableError
                ? mutation.error.message
                : 'An unknown error occurred'
            : undefined,
    };
}
